import {useCallback, useEffect, useState} from "react";
import {AlertTriangle, Megaphone} from "lucide-react";
import {createAdminCmsAnnouncement, deleteAdminCmsAnnouncement, listAdminCmsAnnouncements, updateAdminCmsAnnouncement, type AdminCmsAnnouncement} from "@zaaaz/shared";
import {useAdminConfirm} from "@/hooks/useAdminConfirm";
import {useAdminSectionBadges} from "@/hooks/useAdminSectionBadges";
import {ADMIN_SECTION_NAV} from "@/lib/adminSectionNav";
import {adminSearchText} from "@/lib/adminSearchText";
import {adminToast} from "@/lib/adminToast";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {Switch} from "@/components/ui/switch";
import {AdminAddButton, AdminAlert, AdminBadge, AdminEditModal, AdminField, AdminFormActions, AdminHtmlEditor, AdminModernTable, AdminPageLayout, AdminPagination, AdminSectionTabs, AdminToggleField, AdminRowTrailing} from "./ui";

const emptyForm = {nume: "", continut: "", visible: true, zona: 0};

export function CmsAnnouncementsView() {
  const {confirm, dialog: confirmDialog} = useAdminConfirm();
  const badges = useAdminSectionBadges("continut");
  const [items, setItems] = useState<AdminCmsAnnouncement[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [openId, setOpenId] = useState<number | "new" | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    listAdminCmsAnnouncements({page, pageSize: 20}).then((r) => { setItems(r.items); setTotalPages(r.totalPages); }).catch((e: Error) => setErr(e.message));
  }, [page]);
  useEffect(() => { load(); }, [load]);
  function closeModal(open: boolean) {
    setModalOpen(open);
    if(!open) setOpenId(null);
  }
  function openItem(a: AdminCmsAnnouncement) {
    setOpenId(a.id);
    setForm({nume: a.nume, continut: a.continut, visible: a.visible, zona: a.zona});
    setModalOpen(true);
  }
  function openNew() {
    setOpenId("new");
    setForm(emptyForm);
    setModalOpen(true);
  }
  async function saveItem() {
    try {
        if(openId === "new") {
          await createAdminCmsAnnouncement(form);
          adminToast.success("Announcement added.");
        } else if(typeof openId === "number") {
          await updateAdminCmsAnnouncement(openId, form);
          adminToast.success("Announcement saved.");
        }
      closeModal(false);
      load();
    } catch(e) {
      adminToast.error(e instanceof Error ? e.message : "Save failed");
    }
  }
  function removeItem(id: number) {
    confirm("Delete this announcement?", async () => {
      await deleteAdminCmsAnnouncement(id);
      closeModal(false);
      load();
      adminToast.success("Announcement deleted.");
    }, "Delete", "Delete");
  }
  async function toggleVisible(id: number, visible: boolean) {
    const prev = items;
    setItems((list) => list.map((a) => (a.id === id ? {...a, visible} : a)));
    if(typeof openId === "number" && openId === id) {
      setForm((f) => ({...f, visible}));
    }
    try {
      await updateAdminCmsAnnouncement(id, {visible});
      adminToast.success(visible ? "Announcement activated." : "Announcement deactivated.");
    } catch(e) {
      setItems(prev);
      adminToast.error(e instanceof Error ? e.message : "Could not update announcement");
    }
  }
  if(err) return <AdminAlert variant="error">{err}</AdminAlert>;
  return (
    <AdminPageLayout
      title={ADMIN_SECTION_NAV.continut.title}
      description="Header, cart, and popup announcements"
      breadcrumbs={[
        {label: ADMIN_SECTION_NAV.continut.title, href: "/vadmin/continut"},
        {label: "Announcements"}
      ]}
      actions={<AdminAddButton onClick={openNew} label="Add announcement" />}
    >
      {confirmDialog}
      <AdminSectionTabs section="continut" badges={badges} />
      <AdminModernTable
        items={items}
        getId={(a) => a.id}
        getTitle={(a) => a.nume}
        getSubtitle={(a) => a.continut}
        getMeta={(a) => (
          <>
            {!a.visible ? <AdminBadge variant="danger"><AlertTriangle className="h-3 w-3" /></AdminBadge> : null}
            <AdminBadge variant="outline">{a.zonaLabel}</AdminBadge>
          </>
        )}
        getSearchText={(a) => adminSearchText(a.id, a.nume, a.continut, a.zonaLabel, a.zona, a.visible)}
        selectedId={typeof openId === "number" ? openId : null}
        onRowClick={openItem}
        emptyTitle="No announcements"
        emptyDescription="Add a new announcement."
        searchPlaceholder="Search announcement, content, zone..."
        getRowActions={(a) => (
          <AdminRowTrailing onDelete={() => removeItem(a.id)} deleteLabel="Delete announcement">
            <Switch
              className="scale-90"
              checked={a.visible}
              onCheckedChange={(v) => toggleVisible(a.id, v)}
              aria-label={a.visible ? "Deactivate announcement" : "Activate announcement"}
              title={a.visible ? "Active" : "Inactive"}
            />
          </AdminRowTrailing>
        )}
      />
      <AdminEditModal
        open={modalOpen}
        onOpenChange={closeModal}
        title={openId === "new" ? "New announcement" : "Edit announcement"}
        icon={Megaphone}
        footer={(
          <AdminFormActions>
            <Button type="button" size="sm" onClick={() => saveItem()}>Save</Button>
            {typeof openId === "number" ? (
              <Button type="button" variant="outline" size="sm" onClick={() => removeItem(openId)}>Delete</Button>
            ) : null}
          </AdminFormActions>
        )}
      >
        <div className="admin-detail-panel__form">
          <AdminToggleField
            title="Active announcement"
            description="Inactive announcements are hidden on the storefront."
            checked={form.visible}
            onCheckedChange={(v) => setForm({...form, visible: v})}
          />
          <AdminField label="Name">
            <Input value={form.nume} onChange={(e) => setForm({...form, nume: e.target.value})} />
          </AdminField>
          <AdminField label="Zone">
            <Select value={String(form.zona)} onValueChange={(v) => setForm({...form, zona: Number(v)})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Header</SelectItem>
                <SelectItem value="1">Cos</SelectItem>
                <SelectItem value="2">Popup</SelectItem>
              </SelectContent>
            </Select>
          </AdminField>
          <AdminField label="Content">
            <AdminHtmlEditor value={form.continut} onChange={(html) => setForm({...form, continut: html})} />
          </AdminField>
        </div>
      </AdminEditModal>
      <AdminPagination page={page} totalPages={totalPages} onPrev={() => setPage(page - 1)} onNext={() => setPage(page + 1)} />
    </AdminPageLayout>
  );
}
